import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  type PermissionDefinition,
  type PermissionEntityId,
  type PermissionListItem,
  type PermissionType,
} from '@terros/common'
import { Modal, Stack, Text } from '@mantine/core'
import { NotReady } from '../NotReady'
import { PermGraphViewport } from './PermGraphViewport'
import { PermGraphHeadline } from './PermGraphHeadline'
import {
  PermGraphNodeEditor,
  getPermGraphWinningPath,
  toRenderedPermGraph,
  usePermGraph,
  type PermGraph,
  type PermGraphNode,
} from './graph'

type Props = {
  definition: PermissionDefinition
  entityId: PermissionEntityId
  info: PermissionListItem
  opened: boolean
  permissionType: PermissionType
  readOnly: boolean
  onClose: () => void
}

export const PermTreeModal = ({
  definition,
  entityId,
  info,
  opened,
  permissionType,
  readOnly,
  onClose,
}: Props): ReactNode => {
  const { currentUserId, error, graph, isLoading } = usePermGraph({ enabled: opened, entityId, info, permissionType })
  const [openId, setOpenId] = useState<PermissionEntityId>()

  useEffect(() => {
    if (!opened) setOpenId(undefined)
  }, [opened])

  return (
    <Modal
      centered
      opened={opened}
      classNames={{ body: 'flex min-h-0 flex-1 flex-col', content: 'flex h-[90vh] flex-col' }}
      size='90%'
      title={<Text fw='bold'>{definition.label}</Text>}
      zIndex={300}
      onClose={onClose}
    >
      {isLoading || error || !graph ? (
        <NotReady error={error} isLoading={isLoading} />
      ) : (
        <PermTreeBody
          currentUserId={currentUserId}
          definition={definition}
          entityId={entityId}
          graph={graph}
          openId={openId}
          permissionType={permissionType}
          readOnly={readOnly}
          onToggle={(node) => setOpenId((current) => (current === node.entityId ? undefined : node.entityId))}
        />
      )}
    </Modal>
  )
}

const PermTreeBody = ({
  currentUserId,
  definition,
  entityId,
  graph,
  openId,
  permissionType,
  readOnly,
  onToggle,
}: {
  currentUserId: PermissionEntityId | undefined
  definition: PermissionDefinition
  entityId: PermissionEntityId
  graph: PermGraph
  openId: PermissionEntityId | undefined
  permissionType: PermissionType
  readOnly: boolean
  onToggle: (node: PermGraphNode) => void
}): ReactNode => {
  const rendered = useMemo(() => toRenderedPermGraph(graph), [graph])
  const winningPath = useMemo(() => getPermGraphWinningPath(rendered), [rendered])

  return (
    <Stack className='min-h-0 flex-1' gap='sm'>
      <PermGraphHeadline definition={definition} graph={rendered} />
      <PermGraphViewport
        currentUserId={currentUserId}
        focusedId={openId ?? entityId}
        graph={rendered}
        openId={openId}
        permissionType={permissionType}
        renderEditor={(node) => (
          <PermGraphNodeEditor
            definition={definition}
            node={node}
            permissionType={permissionType}
            readOnly={readOnly}
            viewedEntityId={entityId}
            onClose={() => onToggle(node)}
          />
        )}
        targetId={entityId}
        winningPath={winningPath}
        onToggle={onToggle}
      />
    </Stack>
  )
}
